import { createStart, createMiddleware } from "@tanstack/react-start";
import { compressResponse, negotiateCompression } from "./lib/http/compress";

// Host-aware serving for custom-domain publications (e.g. isaacsaldana.com). On
// Firebase App Hosting every additional domain routes to THIS SSR backend — there is
// no per-host routing to a Cloud Function — so a request middleware resolves the Host.
// App hosts (goodsender.com, dev, previews, localhost) return null and fall through to
// the normal app; every other host is a custom-domain publication and is served its
// blog (or a 404/503) by `serveCustomDomainBlog`, short-circuiting the app — so the
// dashboard/login is unreachable on any domain but goodsender.com. The `.server()`
// body (with its pg-backed dynamic import) is stripped from the client bundle.
// Cache the dynamic import so it happens at most once per instance rather than
// allocating/awaiting a fresh import Promise on every request (this runs for all hosts).
let serverModule: Promise<typeof import("./lib/blog/customDomainServer")> | undefined;
function loadCustomDomainServer() {
  return (serverModule ??= import("./lib/blog/customDomainServer").catch(
    (err) => {
      // Don't cache a rejected import (mirrors loadRenderCore in
      // customDomainServer.ts): a transient init failure would otherwise be reused
      // for every later request, failing all traffic until the instance restarts.
      // Clear the cache so the next request re-attempts the import.
      serverModule = undefined;
      throw err;
    },
  ));
}
// #1075 (US5): every text response leaving this server — the custom-domain blog
// AND the app's own SSR pages — is compressed when the client accepts it.
//
// The two branches can't share the same call, though. `serveCustomDomainBlog`'s
// body is always an ordinary string/ArrayBuffer (`toResponse`'s string bodies;
// `proxySubscribe`'s proxied `ArrayBuffer` — customDomainServer.ts), never the
// router's own stream, so `compressResponse` can stream it straight through.
// `ctx.next()`'s body, on the
// app path, is TanStack Start's backpressure-aware SSR stream
// (`transformStreamWithRouter`'s `makeMainStream`) — piping THAT through any
// extra `ReadableStream`/`TransformStream` layer (compression or not; a bare
// no-op `TransformStream` reproduces it) stalls forever and trips the router's
// own 60s "Serialization timeout after app render finished", because h3's
// response writer no longer reads the router's stream directly. Buffering the
// app response into a plain string first sidesteps that: `Response#text()`
// drains the SAME stream to completion (verified live — headers, curl, and a
// patched `transformStreamWithRouter` confirm it closes normally), and the
// resulting `new Response(text, …)` has an ordinary body `compressResponse` can
// stream-compress without touching the router's stream at all. Only paid when
// compression will actually apply — `negotiateCompression` reuses every one of
// `compressResponse`'s eligibility checks without touching the body, so an
// unencoded request (or one that fails a skip condition) keeps the original
// streamed response untouched. See givip/goodsender-autopilot specs/1058-blog-pagespeed/DECISIONS.md
// (D-1113-3), including the follow-up criteria for removing this workaround.
const customDomainBlogMiddleware = createMiddleware().server(async (ctx) => {
  const { serveCustomDomainBlog } = await loadCustomDomainServer();
  const response = await serveCustomDomainBlog(ctx.request);
  if (response) return compressResponse(ctx.request, response);
  const result = await ctx.next();
  const appResponse = result.response;
  if (!negotiateCompression(ctx.request, appResponse)) {
    return { ...result, response: compressResponse(ctx.request, appResponse) };
  }
  const bytes = await appResponse.arrayBuffer();
  // Now that the body is fully in hand, record its real length — a streamed
  // `ctx.next()` response never carries one (confirmed: neither TanStack Start
  // nor a plain `new Response(string|ArrayBuffer)` sets it), so without this
  // compressResponse's MIN_COMPRESSIBLE_BYTES floor could never fire for this
  // path, Brotli/gzip-wrapping even a tiny error page or server-fn JSON reply.
  const headers = new Headers(appResponse.headers);
  headers.set("content-length", String(bytes.byteLength));
  const buffered = new Response(bytes, {
    status: appResponse.status,
    statusText: appResponse.statusText,
    headers,
  });
  return { ...result, response: compressResponse(ctx.request, buffered) };
});

// D-216-5 (selective SSR): client-only by default; public/SEO routes opt in
// with `ssr: true` (/, /faq, /pricing — the routes prerendered before the
// Start migration). Per-route `ssr` lives on the route files; this instance
// only sets the default.
export const startInstance = createStart(() => ({
  defaultSsr: false,
  requestMiddleware: [customDomainBlogMiddleware],
}));
